import React, { forwardRef, useEffect, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import { v4 as uuidv4 } from "uuid";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import {
  QUILL_LINK_INVALID_EVENT,
  QUILL_LINK_URL_INVALID_HINT,
  quillHyperlinkUrlIsAllowed,
} from "./quillHyperlinkValidation";
import {
  installQuillLinkTooltipSaveValidation,
  upgradeExistingLinkTooltipInputs,
} from "./quillLinkTooltipPatch";

if (typeof window !== "undefined") {
  installQuillLinkTooltipSaveValidation();
}

const sizePickerStyles = `
  .ql-snow .ql-picker.ql-size .ql-picker-label::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item::before {
    content: 'Normal';
  }
  .ql-snow .ql-picker.ql-size .ql-picker-label[data-value="1.8rem"]::before,
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="1.8rem"]::before {
    content: 'Large';
  }
  .ql-snow .ql-picker.ql-size .ql-picker-item[data-value="1.8rem"]::before {
    font-size: 1.8rem;
  }
  .ql-editor {
    font-family: 'Noto Sans', sans-serif;
  }
`;

const Parchment = Quill.import("parchment") as any;

const Embed = Quill.import("blots/embed") as any;

class TabBlot extends Embed {
  static blotName = "tab";
  static tagName = "span";
  static className = "ql-tab";

  static create() {
    const node = super.create() as HTMLElement;
    node.setAttribute("style", "display: inline-block; width: 1rem;");
    node.innerHTML = "&#9;";
    return node;
  }

  static value() {
    return true;
  }

  static formats(node: HTMLElement) {
    const style = node.getAttribute("style") || "";
    if (
      style.includes("display: inline-block") &&
      style.includes("width: 1rem")
    ) {
      return true;
    }
    return undefined;
  }
}

Quill.register(TabBlot, true);

const Delta = Quill.import("delta") as any;

const URL_REGEX = /https?:\/\/[^\s<>"']+/g;
const createUrlRegex = () => new RegExp(URL_REGEX.source, "g");

function trimTrailingPunctuation(url: string): string {
  return url.replace(/[.,;:!?)]+$/, "");
}

function linkifyTextToDelta(
  text: string,
  baseAttributes?: Record<string, unknown>
): any {
  const ops: any[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const re = createUrlRegex();
  while ((match = re.exec(text)) !== null) {
    const url = trimTrailingPunctuation(match[0]);
    const matchEnd = match.index + url.length;
    if (match.index > lastIndex) {
      const plain = text.slice(lastIndex, match.index);
      ops.push(
        baseAttributes
          ? { insert: plain, attributes: baseAttributes }
          : { insert: plain }
      );
    }
    if (quillHyperlinkUrlIsAllowed(url)) {
      ops.push({
        insert: url,
        attributes: { ...baseAttributes, link: url },
      });
    } else {
      ops.push({
        insert: url,
        ...(baseAttributes ? { attributes: baseAttributes } : {}),
      });
    }
    lastIndex = matchEnd;
  }
  if (lastIndex < text.length) {
    const plain = text.slice(lastIndex);
    ops.push(
      baseAttributes
        ? { insert: plain, attributes: baseAttributes }
        : { insert: plain }
    );
  }
  if (ops.length === 0) {
    return baseAttributes
      ? new Delta().insert(text, baseAttributes)
      : new Delta().insert(text);
  }
  return new Delta(ops);
}

function linkifyUrlBeforeCursor(quill: any): boolean {
  const range = quill.getSelection(true);
  if (!range || range.length > 0) return true;
  const index = range.index;
  if (index === 0) return true;
  const textBefore = quill.getText(0, index);
  const re = createUrlRegex();
  let match: RegExpExecArray | null = null;
  let lastMatch: RegExpExecArray | null = null;
  while ((match = re.exec(textBefore)) !== null) {
    lastMatch = match;
  }
  if (!lastMatch) return true;
  const url = trimTrailingPunctuation(lastMatch[0]);
  const urlEnd = lastMatch.index + lastMatch[0].length;
  if (urlEnd !== textBefore.length) return true;
  const format = quill.getFormat(lastMatch.index, url.length);
  if (format?.link) return true;
  if (!quillHyperlinkUrlIsAllowed(url)) return true;
  quill.formatText(lastMatch.index, url.length, "link", url, "user");
  quill.setSelection(index, 0, "user");
  return true;
}

function linkifyDelta(delta: any): any {
  const resultOps: any[] = [];
  const ops = delta.ops ?? [];
  for (const op of ops) {
    if (typeof op.insert === "string") {
      // Skip linkification for ops that already have a link to avoid overwriting href
      if (op.attributes?.link) {
        resultOps.push(op);
      } else {
        const linkified = linkifyTextToDelta(op.insert, op.attributes);
        resultOps.push(...(linkified.ops ?? []));
      }
    } else {
      resultOps.push(op);
    }
  }
  return new Delta(resultOps);
}

/** Removes Quill image embed ops (e.g. from HTML with <img> or mixed clipboard). */
function stripImageEmbedsFromDelta(delta: any): any {
  const ops = delta.ops ?? [];
  const kept: any[] = [];
  for (const op of ops) {
    if (
      op.insert &&
      typeof op.insert === "object" &&
      (op.insert as { image?: unknown }).image !== undefined
    ) {
      continue;
    }
    kept.push(op);
  }
  return new Delta(kept);
}

function handlePasteWithLinkify(
  quill: any,
  event: ClipboardEvent,
  options?: { blockImagePaste?: boolean }
) {
  const blockImagePaste = options?.blockImagePaste === true;
  const clipboardData = event.clipboardData;
  const hasFileItems = Boolean(
    clipboardData?.files?.length ||
      Array.from(clipboardData?.items ?? []).some(
        (item) => item.kind === "file"
      )
  );

  const html = clipboardData?.getData("text/html");
  const text = clipboardData?.getData("text/plain") ?? "";
  const hasRenderableContent = Boolean(html?.trim() || text.trim());

  if (hasFileItems && !blockImagePaste) {
    // Let Quill's default paste handling process file/image clipboard data.
    return;
  }

  if (hasFileItems && blockImagePaste && !hasRenderableContent) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  if (!html?.trim() && !text) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  const range = quill.getSelection(true) ?? {
    index: quill.getLength(),
    length: 0,
  };
  let delta: any;
  if (html && html.trim()) {
    try {
      delta = quill.clipboard.convert({ html, text });
    } catch {
      delta = new Delta().insert(text);
    }
  } else {
    delta = new Delta().insert(text);
  }
  if (blockImagePaste) {
    delta = stripImageEmbedsFromDelta(delta);
  }
  const linkified = linkifyDelta(delta);
  if (linkified.length() === 0) {
    return;
  }
  const toApply = new Delta()
    .retain(range.index)
    .delete(range.length)
    .concat(linkified);
  quill.updateContents(toApply, "user");
  const newIndex = range.index + linkified.length();
  quill.setSelection(newIndex, 0, "user");
}

Quill.register(
  "modules/clipboard",
  class extends (Quill.import("modules/clipboard") as any) {
    constructor(quill: any, options: any) {
      super(quill, options);
      // Do not linkify in the matcher; handlePasteWithLinkify calls linkifyDelta once
      // so linkification happens in a single place and existing links are preserved.
      this.addMatcher(Node.TEXT_NODE, (_node: Node, delta: any) => delta);
      this.addMatcher("span", (node: HTMLElement, delta: any) => {
        const style = node.getAttribute("style") || "";
        if (
          style.includes("display: inline-block") &&
          style.includes("width: 1rem")
        ) {
          return new Delta().insert({ tab: true });
        }
        return delta;
      });
    }
  },
  true
);

const BackgroundStyle = Quill.import("attributors/style/background") as any;
const ColorStyle = Quill.import("attributors/style/color") as any;
const SizeStyle = Quill.import("attributors/style/size") as any;

/** Style-guide approved palette for WYSIWYG text and background color picker */
export const WYSIWYG_STYLE_GUIDE_COLORS = [
  "#FF5D22", // Orange
  "#2B3B72", // Deep Blue
  "#613DC1", // Plump Purple
  "#46B276", // Green
  "#0000B4", // Blue
  "#00AEEF", // Cyan
  "#EC00B4", // Fuchsia
  "#ED1C24", // Red
  "#FFCC3B", // Sunglow
  "#233053", // Space Cadet
  "#F7F9FD", // Ghost White
  "#F3F9FE", // Alice Blue
  "#F1F5FC", // Hover Blue
  "#FFFFFF", // White
  "#232323", // Primary Text Blue
  "#4C5258", // Secondary Text Gray
  "#BDC1CB", // Secondary Text Gray on Dark
  "#9E9E9F", // Disabled
];

if (SizeStyle) {
  SizeStyle.whitelist = ["1rem", "1.8rem"];
}

if (ColorStyle) {
  ColorStyle.whitelist = null; // null means no whitelist on load and paste
}

if (BackgroundStyle) Quill.register(BackgroundStyle, true);
if (ColorStyle) Quill.register(ColorStyle, true);
if (SizeStyle) Quill.register(SizeStyle, true);

const StyleAttributor = SizeStyle?.constructor;

if (StyleAttributor) {
  const IndentStyle = new StyleAttributor("indent", "padding-left", {
    scope: Parchment.Scope.BLOCK,
    whitelist: ["2em", "4em", "6em", "8em", "10em", "12em", "14em", "16em"],
  });

  const originalAdd = IndentStyle.add.bind(IndentStyle);
  const originalValue = IndentStyle.value.bind(IndentStyle);

  IndentStyle.add = function (
    node: HTMLElement,
    value: string | number
  ): boolean {
    if (value === "+1" || value === "-1") {
      const currentIndent = this.value(node) || 0;
      value = value === "+1" ? currentIndent + 1 : currentIndent - 1;
    }
    if (value === 0) {
      this.remove(node);
      return true;
    }
    return originalAdd(node, `${(value as number) * 2}em`);
  };

  IndentStyle.value = function (node: HTMLElement): number | undefined {
    const val = originalValue(node);
    if (!val) return undefined;
    return parseInt(val, 10) / 2;
  };

  Quill.register(IndentStyle, true);
}

interface EnhancedInputFieldProps
  extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  handleChange?: (text: string) => void;
  handleBlur?: (text: string) => void;
  type?: string;
  name: string;
  label?: string;
  classes?: string;
  labelClassName?: string;
  editorClassName?: string;
  placeholder?: string;
  readOnly?: boolean;
  DefaultContentComponent?: React.ComponentType;
  showDefaultContent?: boolean;
  /** When true, pasted images are blocked; text and links still paste normally. */
  blockImagePaste?: boolean;
}

const modules = {
  toolbar: [
    [{ size: [false, "1.8rem"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: WYSIWYG_STYLE_GUIDE_COLORS }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["link"],
  ],
  keyboard: {
    bindings: {
      tab: {
        key: 9,
        handler: function (this: any) {
          const quill = this.quill;
          const range = quill.getSelection();
          if (range) {
            if (range.length > 0) {
              quill.deleteText(range.index, range.length);
            }
            quill.insertEmbed(range.index, "tab", true, "user");
            quill.setSelection(range.index + 1);
          }
          return false;
        },
      },
      "shift+tab": {
        key: 9,
        shiftKey: true,
        handler: function () {
          return false;
        },
      },
      "linkify on space": {
        key: 32,
        handler: function (this: any) {
          return linkifyUrlBeforeCursor(this.quill);
        },
      },
      "linkify on enter": {
        key: "Enter",
        handler: function (this: any) {
          return linkifyUrlBeforeCursor(this.quill);
        },
      },
      "linkify on comma": {
        key: 188,
        handler: function (this: any) {
          return linkifyUrlBeforeCursor(this.quill);
        },
      },
    },
  },
};

const formats = [
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "indent",
  "link",
  "image",
  "tab",
];

export const EnhancedRichTextEditor = forwardRef<
  HTMLTextAreaElement,
  EnhancedInputFieldProps
>(
  (
    {
      handleChange,
      type,
      name,
      classes,
      handleBlur,
      editorClassName,
      placeholder,
      readOnly,
      DefaultContentComponent,
      showDefaultContent,
      blockImagePaste,
      ...rest
    },
    ref
  ) => {
    const [content, setContent] = useState<string>(
      rest.value?.toString() ?? ""
    );
    const lastPropValueRef = useRef<string>(rest.value?.toString() ?? "");
    const isSyncingRef = useRef(false);
    const [quillContainerId] = useState(() => `quillcontainer-${uuidv4()}`);
    const isUpdatingFromProps = useRef(false);
    const previousPropValue = useRef<string>(rest.value?.toString() ?? "");
    const reactQuillRef = useRef<InstanceType<typeof ReactQuill> | null>(null);
    const pasteCleanupRef = useRef<(() => void) | null>(null);
    const blockImagePasteRef = useRef(Boolean(blockImagePaste));
    blockImagePasteRef.current = Boolean(blockImagePaste);
    const { showPushToast: showLinkEditorInvalidUrlNotice } =
      useApplicationToast();
    const showLinkEditorInvalidUrlNoticeRef = useRef(
      showLinkEditorInvalidUrlNotice
    );
    showLinkEditorInvalidUrlNoticeRef.current = showLinkEditorInvalidUrlNotice;

    useEffect(() => {
      let cancelled = false;
      let cleanup: (() => void) | undefined;
      const trySubscribe = () => {
        if (cancelled) return false;
        const quill = reactQuillRef.current?.getEditor?.();
        const root = quill?.root;
        if (!root) return false;
        upgradeExistingLinkTooltipInputs();
        const onInvalid = () => {
          showLinkEditorInvalidUrlNoticeRef.current?.({
            message: QUILL_LINK_URL_INVALID_HINT,
            type: "warning",
          });
        };
        root.addEventListener(QUILL_LINK_INVALID_EVENT, onInvalid);
        cleanup = () =>
          root.removeEventListener(QUILL_LINK_INVALID_EVENT, onInvalid);
        return true;
      };
      if (trySubscribe())
        return () => {
          cancelled = true;
          cleanup?.();
        };
      const id = window.setInterval(() => {
        if (trySubscribe()) {
          window.clearInterval(id);
        }
      }, 50);
      const maxWait = window.setTimeout(() => window.clearInterval(id), 5000);
      return () => {
        cancelled = true;
        window.clearInterval(id);
        window.clearTimeout(maxWait);
        cleanup?.();
      };
    }, []);

    useEffect(() => {
      const id = setTimeout(() => {
        const quill = reactQuillRef.current?.getEditor?.();
        if (!quill?.root) return;
        const handler = (e: ClipboardEvent) =>
          handlePasteWithLinkify(quill, e, {
            blockImagePaste: blockImagePasteRef.current,
          });
        quill.root.addEventListener("paste", handler, true);
        pasteCleanupRef.current = () => {
          quill.root.removeEventListener("paste", handler, true);
        };
      }, 0);
      return () => {
        clearTimeout(id);
        pasteCleanupRef.current?.();
        pasteCleanupRef.current = null;
      };
    }, [blockImagePaste]);

    function linkifyTypedUrls() {
      const editor = reactQuillRef.current?.getEditor?.();
      if (!editor) return content;
      const text = editor.getText();
      const matches: { index: number; length: number; url: string }[] = [];
      let match: RegExpExecArray | null;
      const re = createUrlRegex();
      while ((match = re.exec(text)) !== null) {
        const url = trimTrailingPunctuation(match[0]);
        matches.push({
          index: match.index,
          length: url.length,
          url,
        });
      }
      for (let i = matches.length - 1; i >= 0; i--) {
        const { index, length, url } = matches[i];
        const format = editor.getFormat?.(index, length);
        if (format?.link) continue;
        if (!quillHyperlinkUrlIsAllowed(url)) continue;
        // Blur-time linkification should not emit intermediary user changes.
        editor.formatText(index, length, "link", url, "silent");
      }
      const updatedContent = editor.root?.innerHTML ?? content;
      if (updatedContent !== content) {
        setContent(updatedContent);
      }
      return updatedContent;
    }

    useEffect(() => {
      const newValue = rest.value?.toString() ?? "";
      const prevPropValue = previousPropValue.current;

      if (prevPropValue !== newValue) {
        isUpdatingFromProps.current = true;
        previousPropValue.current = newValue;
        setContent(newValue);

        Promise.resolve().then(() => {
          requestAnimationFrame(() => {
            isUpdatingFromProps.current = false;
          });
        });
      }
    }, [rest.value]);

    useEffect(() => {
      if (isSyncingRef.current && content === lastPropValueRef.current) {
        isSyncingRef.current = false;
      }
    }, [content]);

    function onChange(value: string) {
      if (isSyncingRef.current && value === lastPropValueRef.current) {
        return;
      }
      if (value === content) {
        return;
      }
      setContent(value);
      const currentPropValue = rest.value?.toString() ?? "";
      if (!isUpdatingFromProps.current && value !== currentPropValue) {
        handleChange?.(value);
      }
    }

    const shouldShowDefault =
      Boolean(DefaultContentComponent) && Boolean(showDefaultContent);

    return (
      <div className={classes ?? "w-full"}>
        <div id={quillContainerId} className="relative">
          <style>{sizePickerStyles}</style>
          <ReactQuill
            ref={reactQuillRef}
            value={content}
            onChange={onChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
            className={editorClassName}
            bounds={`#${quillContainerId}`}
            onBlur={() => {
              const updatedContent = !readOnly ? linkifyTypedUrls() : content;
              handleBlur?.(updatedContent);
            }}
            readOnly={readOnly}
          />
          {shouldShowDefault && DefaultContentComponent && (
            <div className="pointer-events-none absolute inset-0 overflow-auto px-4 py-3 text-gray-500">
              <DefaultContentComponent />
            </div>
          )}
        </div>
      </div>
    );
  }
);
